/**
 * @fileoverview Calendar Toolbar Component
 * @description Navigation and filter controls for the calendar
 */

import React from 'react';
import {
  Box, Button, IconButton, Typography, TextField, MenuItem,
  ToggleButton, ToggleButtonGroup, Chip, Tooltip,
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, Plus, RefreshCw, Calendar,
  Filter,
} from 'lucide-react';
import { RequirePermission } from '@/components';
import { MODULES } from '@/constants';
import { CALENDAR_VIEWS, EVENT_STATUSES, EVENT_STATUS_LABELS, PRIORITY_OPTIONS } from '../../constants';
import { getViewTitle } from '../../utils';

const CalendarToolbar = ({
  currentDate,
  view,
  onNavigate,
  onViewChange,
  onTodayClick,
  onCreateClick,
  onRefresh,
  loading,
  // Filters
  filters,
  onFilterChange,
  teams,
  testTypes,
  environments,
}) => {
  return (
    <Box className="calendar-toolbar">
      {/* Row 1: Navigation + View Toggle + Create */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {/* Left: Nav */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={onTodayClick}
            sx={{ textTransform: 'none', fontWeight: 600, mr: 1 }}
          >
            Today
          </Button>
          <IconButton size="small" onClick={() => onNavigate('prev')}>
            <ChevronLeft size={20} />
          </IconButton>
          <IconButton size="small" onClick={() => onNavigate('next')}>
            <ChevronRight size={20} />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#01426A', ml: 1, whiteSpace: 'nowrap' }}>
            {getViewTitle(currentDate, view)}
          </Typography>
        </Box>

        {/* Right: View toggle + Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, val) => val && onViewChange(val)}
            size="small"
          >
            <ToggleButton value={CALENDAR_VIEWS.MONTH} sx={{ textTransform: 'none', px: 2 }}>
              Month
            </ToggleButton>
            <ToggleButton value={CALENDAR_VIEWS.WEEK} sx={{ textTransform: 'none', px: 2 }}>
              Week
            </ToggleButton>
            <ToggleButton value={CALENDAR_VIEWS.DAY} sx={{ textTransform: 'none', px: 2 }}>
              Day
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={onRefresh} size="small" disabled={loading}>
                <RefreshCw size={18} className={loading ? 'spin' : ''} />
              </IconButton>
            </span>
          </Tooltip>

          <RequirePermission module={MODULES.TEST_CALENDAR} operation="write">
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={onCreateClick}
              sx={{
                backgroundColor: '#01426A',
                '&:hover': { backgroundColor: '#002F5F' },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              New Event
            </Button>
          </RequirePermission>
        </Box>
      </Box>

      {/* Row 2: Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Filter size={16} style={{ color: '#6B7280' }} />

        <TextField
          select
          label="Status"
          value={filters.status || ''}
          onChange={(e) => onFilterChange('status', e.target.value)}
          size="small"
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">All</MenuItem>
          {Object.entries(EVENT_STATUS_LABELS).map(([key, label]) => (
            <MenuItem key={key} value={key}>{label}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Priority"
          value={filters.priority || ''}
          onChange={(e) => onFilterChange('priority', e.target.value)}
          size="small"
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">All</MenuItem>
          {PRIORITY_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Team"
          value={filters.team_id || ''}
          onChange={(e) => onFilterChange('team_id', e.target.value)}
          size="small"
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All Teams</MenuItem>
          {(Array.isArray(teams) ? teams : []).map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.team_name}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Test Type"
          value={filters.test_type_id || ''}
          onChange={(e) => onFilterChange('test_type_id', e.target.value)}
          size="small"
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All Types</MenuItem>
          {(Array.isArray(testTypes) ? testTypes : []).map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.type_name}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Environment"
          value={filters.environment_id || ''}
          onChange={(e) => onFilterChange('environment_id', e.target.value)}
          size="small"
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All Envs</MenuItem>
          {(Array.isArray(environments) ? environments : []).map((e) => (
            <MenuItem key={e.id} value={e.id}>{e.environment_name}</MenuItem>
          ))}
        </TextField>

        <TextField
          label="Search"
          value={filters.search || ''}
          onChange={(e) => onFilterChange('search', e.target.value)}
          size="small"
          placeholder="Search events..."
          sx={{ minWidth: 160 }}
        />

        {/* Active filter count */}
        {Object.values(filters).filter(Boolean).length > 0 && (
          <Chip
            label="Clear Filters"
            size="small"
            onDelete={() => {
              ['status', 'priority', 'team_id', 'test_type_id', 'environment_id', 'search'].forEach(
                (key) => onFilterChange(key, '')
              );
            }}
            sx={{ fontSize: '0.7rem' }}
          />
        )}
      </Box>
    </Box>
  );
};

export { CalendarToolbar };
export default CalendarToolbar;
